import React, { useCallback, useEffect, useState } from 'react';
import { fetchUsers, createUser, updateUser, deleteUser } from '../../api/users';
import { fetchStores } from '../../api/stores';
import { getErrorMessage } from '../../api/client';

const ROLES = ['admin', 'manager', 'cashier', 'viewer'];

export default function UsersManager({ authConfig, onUnauthorized }) {
  const [users, setUsers] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [form, setForm] = useState({
    username: '',
    password: '',
    role: 'cashier',
    storeIds: [],
    active: true
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [userList, storeList] = await Promise.all([
        fetchUsers(authConfig),
        fetchStores().catch(() => [])
      ]);
      setUsers(Array.isArray(userList) ? userList : []);
      setStores(Array.isArray(storeList) ? storeList : []);
    } catch (err) {
      if (err?.response?.status === 401 && onUnauthorized) onUnauthorized(err);
      setError(getErrorMessage(err) || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [authConfig, onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'storeIds') {
      const opts = e.target.selectedOptions;
      const ids = Array.from(opts).map((o) => o.value).filter(Boolean);
      setForm((f) => ({ ...f, storeIds: ids }));
      return;
    }
    setForm((f) => ({
      ...f,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const resetForm = () => {
    setEditingId(null);
    setShowAddModal(false);
    setForm({ username: '', password: '', role: 'cashier', storeIds: [], active: true });
  };

  const handleEdit = (user) => {
    setEditingId(user._id);
    setShowAddModal(false);
    setForm({
      username: user.username || '',
      password: '',
      role: user.role || 'cashier',
      storeIds: user.storeIds || [],
      active: user.active !== false
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const payload = {
      role: form.role,
      storeIds: form.storeIds,
      active: form.active
    };
    if (editingId) {
      if (form.password) payload.password = form.password;
    } else {
      if (!form.username.trim()) {
        setError('Username is required');
        return;
      }
      if (!form.password) {
        setError('Password is required for new user');
        return;
      }
      payload.username = form.username.trim();
      payload.password = form.password;
    }
    try {
      if (editingId) {
        await updateUser(editingId, payload, authConfig);
      } else {
        await createUser(payload, authConfig);
      }
      resetForm();
      await load();
    } catch (err) {
      if (err?.response?.status === 401 && onUnauthorized) onUnauthorized(err);
      setError(getErrorMessage(err) || 'Failed to save user');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    setError('');
    try {
      await deleteUser(id, authConfig);
      if (editingId === id) resetForm();
      await load();
    } catch (err) {
      if (err?.response?.status === 401 && onUnauthorized) onUnauthorized(err);
      setError(getErrorMessage(err) || 'Failed to delete user');
    }
  };

  const storeNameById = stores.reduce((acc, store) => {
    const id = store._id || store.id;
    if (id) acc[String(id)] = store.name || id;
    return acc;
  }, {});

  const getUserStores = (user) => {
    const ids = Array.isArray(user.storeIds) ? user.storeIds : [];
    if (ids.length === 0) return 'All stores';
    return ids.map((id) => storeNameById[String(id)] || String(id)).join(', ');
  };

  const getInitials = (username = '') => {
    const parts = String(username).trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return String(username).slice(0, 2).toUpperCase() || 'U';
  };

  const renderUserActions = (user) => (
    <div className="flex gap-2">
      <button type="button" onClick={() => handleEdit(user)} className="flex-1 text-sm border px-3 py-2 rounded hover:bg-gray-50">
        Edit
      </button>
      <button type="button" onClick={() => handleDelete(user._id)} className="flex-1 text-sm bg-red-600 text-white px-3 py-2 rounded hover:bg-red-700">
        Delete
      </button>
    </div>
  );

  const renderUserForm = ({ submitLabel, showUsername = true }) => (
    <form onSubmit={handleSubmit} className="space-y-3 p-5">
      {showUsername && (
        <input
          name="username"
          placeholder="Username"
          className="border w-full p-2 rounded"
          value={form.username}
          onChange={handleChange}
          required
          disabled={!!editingId}
        />
      )}
      <input
        name="password"
        type="password"
        placeholder={editingId ? 'New password (leave blank to keep)' : 'Password'}
        className="border w-full p-2 rounded"
        value={form.password}
        onChange={handleChange}
        required={!editingId}
      />
      <select name="role" className="border w-full p-2 rounded" value={form.role} onChange={handleChange}>
        {ROLES.map((r) => (
          <option key={r} value={r}>{r}</option>
        ))}
      </select>
      <div>
        <label className="block text-sm text-gray-600 mb-1">Stores (optional)</label>
        <select
          name="storeIds"
          multiple
          className="border w-full p-2 rounded min-h-[96px]"
          value={form.storeIds}
          onChange={handleChange}
        >
          {stores.map((s) => (
            <option key={s._id || s.id} value={s._id || s.id}>{s.name || s._id}</option>
          ))}
        </select>
      </div>
      <label className="flex items-center gap-2">
        <input type="checkbox" name="active" checked={form.active} onChange={handleChange} />
        <span>Active</span>
      </label>
      <div className="flex gap-2 pt-2">
        <button type="submit" className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded text-sm hover:bg-indigo-500">
          {submitLabel}
        </button>
        <button type="button" onClick={resetForm} className="flex-1 border px-4 py-2 rounded text-sm hover:bg-gray-50">
          Cancel
        </button>
      </div>
    </form>
  );

  if (loading) return <p>Loading users...</p>;

  return (
    <div className="space-y-6">
      {error && <p className="text-red-600 text-sm">{error}</p>}
      <div>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h4 className="font-medium">Users</h4>
            <p className="text-xs text-gray-500">Manage admin, cashier, and store access accounts.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                resetForm();
                setShowAddModal(true);
              }}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500"
              title="Add user"
            >
              <span className="text-lg leading-none">+</span>
              Add
            </button>
            <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 text-sm">
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className={`rounded-md px-3 py-1.5 ${viewMode === 'list' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                List
              </button>
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                className={`rounded-md px-3 py-1.5 ${viewMode === 'grid' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                Grid
              </button>
            </div>
          </div>
        </div>

        {users.length === 0 ? (
          <div className="rounded border p-3 text-gray-500">No users yet.</div>
        ) : viewMode === 'grid' ? (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {users.map((u) => (
              <div key={u._id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="mb-4 flex items-start gap-3">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-lg font-bold text-indigo-700">
                    {getInitials(u.username)}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-gray-900">{u.username}</p>
                    <p className="text-sm capitalize text-gray-600">{u.role}</p>
                    <p className="mt-1 truncate text-xs text-gray-500" title={getUserStores(u)}>{getUserStores(u)}</p>
                    <span className={`mt-2 inline-flex rounded-full px-2 py-0.5 text-xs ${u.active === false ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                      {u.active === false ? 'Inactive' : 'Active'}
                    </span>
                  </div>
                </div>
                {renderUserActions(u)}
              </div>
            ))}
          </div>
        ) : (
          <ul className="border rounded divide-y">
            {users.map((u) => (
              <li key={u._id} className="p-3 flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-sm font-bold text-indigo-700">
                    {getInitials(u.username)}
                  </div>
                  <div className="min-w-0">
                    <span className="font-medium">{u.username}</span>
                    <span className="text-sm text-gray-500 ml-2">({u.role})</span>
                    {!u.active && <span className="text-amber-600 text-sm ml-2">Inactive</span>}
                    <p className="truncate text-xs text-gray-500">{getUserStores(u)}</p>
                  </div>
                </div>
                <div className="w-44 shrink-0">{renderUserActions(u)}</div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {showAddModal && !editingId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6" role="dialog" aria-modal="true" aria-labelledby="add-user-title">
          <div className="w-full max-w-md rounded-xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <h4 id="add-user-title" className="font-semibold text-gray-900">Add user</h4>
              <button type="button" onClick={resetForm} className="text-2xl leading-none text-gray-400 hover:text-gray-700" aria-label="Close add user">×</button>
            </div>
            {renderUserForm({ submitLabel: 'Add user' })}
          </div>
        </div>
      )}

      {editingId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6" role="dialog" aria-modal="true" aria-labelledby="edit-user-title">
          <div className="w-full max-w-md rounded-xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <h4 id="edit-user-title" className="font-semibold text-gray-900">Edit user</h4>
              <button type="button" onClick={resetForm} className="text-2xl leading-none text-gray-400 hover:text-gray-700" aria-label="Close edit user">×</button>
            </div>
            {renderUserForm({ submitLabel: 'Update user' })}
          </div>
        </div>
      )}
    </div>
  );
}
